import { useState } from 'react';
import { Flight } from '../types';

interface FlightSelectorProps {
  flights: Flight[];
  selectedFlight: Flight | null;
  onSelect: (flight: Flight) => void;
}

export function FlightSelector({ flights, selectedFlight, onSelect }: FlightSelectorProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="w-full">
      <label className="block text-xs font-mono text-orbital-cyan uppercase tracking-widest mb-3">
        Select Destination [FLIGHT_SELECT]
      </label>

      {/* Selected flight display */}
      {selectedFlight && !isExpanded && (
        <div
          className="cursor-pointer bg-orbital-card border border-orbital-dim hover:border-orbital-primary p-4 transition-all duration-300"
          onClick={() => setIsExpanded(true)}
        >
          <div className="flex items-start gap-4">
            <span className="text-3xl">{selectedFlight.icon}</span>
            <div className="flex-1">
              <div className="flex justify-between items-start">
                <h3 className="font-mono text-orbital-primary text-lg">{selectedFlight.destination}</h3>
                <span className="text-xs text-orbital-magenta animate-pulse">CLICK TO CHANGE</span>
              </div>
              <p className="text-sm text-gray-400 mt-1">{selectedFlight.description}</p>
              <div className="flex gap-4 mt-2 text-xs text-gray-500 font-mono">
                <span>DEPART: {selectedFlight.departure}</span>
                <span>RETURN: {selectedFlight.returnDate}</span>
                <span>DURATION: {selectedFlight.duration}</span>
              </div>
              <div className="mt-2 text-orbital-yellow font-mono">
                ${selectedFlight.price.toLocaleString()} USD — {selectedFlight.class.toUpperCase()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Expandable flight list */}
      {(isExpanded || !selectedFlight) && (
        <div className="space-y-3">
          {flights.map((flight) => (
            <div
              key={flight.id}
              className={`cursor-pointer border p-4 transition-all duration-200 ${
                selectedFlight?.id === flight.id
                  ? 'border-orbital-primary bg-orbital-primary/10 shadow-[0_0_20px_rgba(0,212,255,0.2)]'
                  : 'border-orbital-dim hover:border-orbital-cyan bg-orbital-card'
              }`}
              onClick={() => {
                onSelect(flight);
                setIsExpanded(false);
              }}
            >
              <div className="flex items-start gap-4">
                <span className="text-2xl">{flight.icon}</span>
                <div className="flex-1">
                  <div className="flex justify-between items-start">
                    <h3 className="font-mono text-base text-orbital-cyan">{flight.destination}</h3>
                    <span className="text-xs px-2 py-0.5 border border-orbital-magenta text-orbital-magenta">
                      {flight.class.toUpperCase()}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">{flight.description}</p>
                  <div className="flex gap-4 mt-2 text-xs text-gray-600 font-mono">
                    <span>DEP: {flight.departure}</span>
                    <span>RET: {flight.returnDate}</span>
                    <span>DUR: {flight.duration}</span>
                    <span className={flight.availableSeats < 4 ? 'text-red-500' : ''}>
                      SEATS: {flight.availableSeats}
                    </span>
                  </div>
                  <div className="mt-2 text-orbital-yellow font-mono text-sm">
                    ${flight.price.toLocaleString()} USD
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
